/**
 * AddParentSheet — invite a co-owner (Plan 06).
 *
 * The Add Parent path of `AddMemberSheet`'s picker mode (guardian invites
 * from the Guardians card's "+ Add guardian" use `<InviteCoParentSheet />`).
 *
 * Adult invites require both an email (for the magic-link delivery)
 * and a cell phone (text-message comms is core to Ordo operations,
 * per Plan 06's edge-case rules). The form validates both client-
 * side before submit.
 *
 * The actual dispatch — creating an `AccountCoOwner` pending row
 * (Plan 10) and a Boombox invitation (Plan 36) — lands when those
 * plans ship. v1 collects the input and surfaces a friendly
 * "we'll save your invite and dispatch it when the channel is
 * ready" hint via toast so the UX is in place the day the wires
 * connect.
 */

import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { toast } from '@ordo/ui/sonner'
import { useState } from 'react'

import { isPlausibleEmail, isPlausiblePhone } from './helpers'

export interface AddParentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful (mock) invite. */
  onInvited?: () => void
}

interface ParentFormState {
  firstName: string
  lastName: string
  email: string
  phone: string
}

const INITIAL: ParentFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
}

export function AddParentSheet({ open, onOpenChange, onInvited }: AddParentSheetProps) {
  const [form, setForm] = useState<ParentFormState>(INITIAL)
  const [errors, setErrors] = useState<Partial<Record<keyof ParentFormState, string>>>({})

  function update<K extends keyof ParentFormState>(key: K, value: ParentFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!prev[key]) {
        return prev
      }
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  function validate(): boolean {
    const next: Partial<Record<keyof ParentFormState, string>> = {}
    if (!form.firstName.trim()) {
      next.firstName = 'Required'
    }
    if (!form.lastName.trim()) {
      next.lastName = 'Required'
    }
    if (!isPlausibleEmail(form.email)) {
      next.email = 'Enter a valid email so we can deliver the magic link.'
    }
    if (!isPlausiblePhone(form.phone)) {
      next.phone = 'A cell phone is required so we can text invite + alerts.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit() {
    if (!validate()) {
      throw new Error('Fix the highlighted fields and try again.')
    }
    // Real dispatch lands with Plan 10 + Plan 36. v1 surfaces a hint.
    toast.success('Co-owner invite queued', {
      description:
        "We'll save the details and dispatch the invitation once the co-ownership flow ships. You can edit pending invites from /billing/family-balance.",
    })
    setForm(INITIAL)
    setErrors({})
    onInvited?.()
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Invite a co-owner"
      description="Add another adult to this household — they'll get a magic-link invite to manage students and orders alongside you."
      submitLabel="Send invite"
      onSubmit={handleSubmit}
      onReset={() => {
        setForm(INITIAL)
        setErrors({})
      }}
      size="md">
      <FormField label="First name" htmlFor="add-parent-first" required error={errors.firstName}>
        <Input
          id="add-parent-first"
          value={form.firstName}
          onChange={(e) => update('firstName', e.target.value)}
          autoComplete="given-name"
          placeholder="Riley"
        />
      </FormField>
      <FormField label="Last name" htmlFor="add-parent-last" required error={errors.lastName}>
        <Input
          id="add-parent-last"
          value={form.lastName}
          onChange={(e) => update('lastName', e.target.value)}
          autoComplete="family-name"
          placeholder="Patel"
        />
      </FormField>
      <FormField
        label="Email"
        htmlFor="add-parent-email"
        required
        error={errors.email}
        helperText="We send the invitation as a magic link to this address.">
        <Input
          id="add-parent-email"
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          autoComplete="email"
          placeholder="riley@example.com"
        />
      </FormField>
      <FormField
        label="Cell phone"
        htmlFor="add-parent-phone"
        required
        error={errors.phone}
        helperText="Required for adult co-owners — we text about meal cutoffs and unserved-meal flags.">
        <Input
          id="add-parent-phone"
          type="tel"
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          autoComplete="tel"
          placeholder="+1 (555) 123-4567"
        />
      </FormField>
      <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">Co-owners can:</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>Place orders for any student on the account.</li>
          <li>Edit dietary preferences + school info.</li>
          <li>Manage their own sign-in credentials.</li>
        </ul>
        <p className="mt-2">They cannot remove the account owner. To transfer ownership, contact Ordo support.</p>
      </div>
    </FormSheet>
  )
}
